import React, { useState, useMemo } from 'react';
import {
    FiCheckCircle,
    FiXCircle,
    FiMinusCircle,
    FiChevronDown,
    FiBookmark
} from "react-icons/fi";

const QuestionNavigator = ({
    questions = [],
    activeIndex = 0,
    setActiveIndex
}) => {

    const [activeStatus, setActiveStatus] = useState('All');
    const [activeSection, setActiveSection] = useState('All Sections');


    /**
     ✅ derive sections safely
     */
    const sections = useMemo(() => {

        if (!questions.length) return ['All Sections'];

        return [
            'All Sections',
            ...new Set(questions.map(q => q.subject))
        ];

    }, [questions]);


    /**
     ✅ FAST STATUS DERIVATION
     NO correctOption dependency needed.
     */
    const getStatus = (q) => {

        if (q.isSkipped) return 'skipped';

        if (!q.userAnswer) return 'skipped';

        return q.isCorrect ? 'correct' : 'incorrect';
    };

    // const getStatus = (q) => {
    //     if (q.isMarkedForReview) return 'review';

    //     if (!q.selectedOption) return 'skipped';

    //     return q.isCorrect ? 'correct' : 'incorrect';
    // };

    const getPrimaryStatus = (q) => {

        if (q.isSkipped === true)
            return 'skipped';

        if (!q.userAnswer)
            return 'skipped';

        if (q.isCorrect === true)
            return 'correct';

        if (q.isCorrect === false)
            return 'incorrect';

        return 'skipped';
    };


    /**
     ✅ FILTER with ORIGINAL INDEX
     VERY IMPORTANT PATTERN
     prevents findIndex()
     */
    const filteredQuestions = useMemo(() => {

        return questions
            .map((q, index) => ({
                ...q,
                originalIndex: index
            }))
            .filter((q) => {

                const status = getStatus(q);

                const statusMatch =
                    activeStatus === 'All' ||
                    status === activeStatus.toLowerCase();

                const sectionMatch =
                    activeSection === 'All Sections' ||
                    q.subject === activeSection;

                return statusMatch && sectionMatch;
            });

    }, [questions, activeStatus, activeSection]);


    return (
        <div className="w-full lg:w-[280px] flex flex-col gap-6 shrink-0 font-inter">

            {/* QUESTION NAVIGATOR */}
            <div className="bg-white border border-[#E0E0E0] rounded-[10px] overflow-hidden shadow-sm">

                <div className="bg-[#4096F6] text-white py-3 text-center font-[500] text-[16px]">
                    Question Navigator
                </div>

                <div className="p-4">

                    {/* Filter by Section */}
                    <label className="text-[16px] font-[500] text-[#000000] mb-2 block">
                        Filter by Section
                    </label>

                    <div className="relative mb-4">
                        <select
                            value={activeSection}
                            onChange={(e) => setActiveSection(e.target.value)}
                            className="w-full border border-[#000000] rounded-[5px] p-2 pr-10 text-[14px] font-[500] outline-none bg-white appearance-none"
                        >
                            {sections.map(section => (
                                <option key={section}>
                                    {section}
                                </option>
                            ))}
                        </select>

                        <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-[#000000] pointer-events-none" />
                    </div>

                    {/* Filter by Status */}
                    <label className="text-[14px] font-[500] text-[#000000] mb-2 block">
                        Filter by Status
                    </label>

                    <div className="flex gap-1 mb-6">
                        {['All', 'Correct', 'Incorrect', 'Skipped', 'Review'].map((tag) => (
                            <button
                                key={tag}
                                onClick={() => setActiveStatus(tag)}
                                className={`flex-1 text-[12px] font-[500] py-1.5 rounded-full border transition-colors
                                ${activeStatus === tag
                                        ? 'bg-[#4096F6] text-white border-[#4096F6]'
                                        : 'bg-white border-[#000000] text-[#000000] hover:bg-gray-50'
                                    }`}
                            >
                                {tag}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Question List */}
                <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1 custom-scrollbar">

                    {filteredQuestions.map((q) => {

                        const primaryStatus = getPrimaryStatus(q);
                        const isReview = q.isMarkedForReview;
                        const isActive = q.originalIndex === activeIndex;

                        return (
                            <div
                                key={q.id}
                                onClick={() => setActiveIndex(q.originalIndex)}
                                className={`flex items-center gap-3 p-2.5 border-b border-[#000000B2] cursor-pointer transition-all
                                ${isActive
                                        ? 'bg-[#FEE2E2] border-l-4 border-red-500'
                                        : 'bg-white hover:border-[#4096F6]'
                                    }`}
                            >

                                {primaryStatus === 'correct' && (
                                    <FiCheckCircle className="text-[#21C11E] text-lg shrink-0" />
                                )}

                                {primaryStatus === 'incorrect' && (
                                    <FiXCircle className="text-[#E63946] text-lg shrink-0" />
                                )}

                                {primaryStatus === 'skipped' && (
                                    <FiMinusCircle className="text-gray-400 text-lg shrink-0" />
                                )}

                                {isReview && (
                                    <FiBookmark className="text-orange-500 text-lg shrink-0" />
                                )}



                                <div className="text-left leading-none">
                                    <p className="text-[14px] font-[500] text-[#000000]">
                                        Q{q.questionNo}
                                    </p>

                                    <p className="text-[12px] font-[400] text-[#00000099] mt-1">
                                        {q.subject}
                                    </p>
                                </div>

                            </div>
                        );
                    })}
                </div>

                <div className="flex justify-center mt-2">
                    <FiChevronDown className="text-gray-400" />
                </div>
            </div>

            {/* QUICK ACTIONS */}
            <div className="bg-white border border-[#E0E0E0] rounded-[10px] overflow-hidden shadow-sm">

                <div className="bg-[#4096F6] text-white py-3 text-center font-bold text-[16px]">
                    Quick Actions
                </div>

                <div className="p-4 flex flex-col flex-wrap gap-3">

                    <button
                        onClick={() => setActiveStatus('Incorrect')}
                        className="w-full bg-[#E63946] text-white py-2.5 rounded-[5px] font-bold text-[14px] flex items-center justify-center gap-2 hover:bg-[#d4313d] transition-all"
                    >
                        <FiXCircle className="text-lg" />
                        View Incorrect Only
                    </button>

                    <button
                        className="w-full bg-white border border-[#E63946] text-[#E63946] py-2.5 rounded-[5px] font-bold text-[14px] flex items-center justify-center gap-2 hover:bg-red-50 transition-all"
                    >
                        <FiBookmark className="text-lg" />
                        View Bookmarked
                    </button>

                </div>
            </div>

        </div>
    );
};

export default QuestionNavigator;
